import { useAtomValue } from 'jotai';
import { moduleFlagsAtom } from '../Atoms';

/**
 * The value of an install-scoped module flag, e.g. `MASSIVE_FEATURES`.
 *
 * Replaces `checkFeature(name)`, which read `window.databeasy` injected by
 * `env.sh` at container start. Three things change:
 *
 * 1. **A flag has a declared default.** `checkFeature` answered false for any name
 *    it did not recognise, so a misspelt flag silently disabled a feature and
 *    nothing said so. A name no module declares is now simply absent here, and
 *    `fallback` is what answers — visibly, at the call site.
 * 2. **It comes from the server**, so an operator changes it in SettingsPage
 *    without a redeploy, and a disabled module's flags are never served at all.
 * 3. **It is reactive.** The old reader was a synchronous global, so a value that
 *    arrived late produced no re-render. This is an atom.
 *
 * Not a permission. Flags say what an installation bought; permissions say what a
 * user may do, are enforced server-side, and are read with `useHasPermission`.
 *
 * @param {string} name flag name as declared on the owning module
 * @param {*} fallback answer while the bootstrap payload is in flight, or if no
 *   enabled module declares `name`
 */
export default function useModuleFlag(name, fallback = false) {
  const flags = useAtomValue(moduleFlagsAtom);
  if (!flags || !Object.hasOwn(flags, name)) return fallback;
  return flags[name] ?? fallback;
}
